import classNames from 'clsx';
import { get } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import URL from 'url-parse';

import { getIconFallback, getIconWidth, getRenderIcon, iconMeta, needsVideoTag } from '@swf/casino';
import CasinoMultiGameImage from '@swf/components/CasinoMultiGameImage/CasinoMultiGameImage';
import LazyImage from '@swf/components/LazyImage/LazyImage';
import FSBIcon from '@swf/core/assets/casino/fsb-placeholder.png';
import useTouchSupported from '@swf/core/hooks/useTouchSupported';
import { select } from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import { CasinoIcon } from '@swf/generated/types/common/Casino';

import styles from './CasinoGameIcon.scss';

export interface Props {
  className?: string;
  disabled?: boolean;
  game?: CasinoGame;
  race?: CasinoRace;
  FSBPlaceholder?: boolean;
  icon?: CasinoIcon;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  preload?: boolean;
  ref?: React.Ref<HTMLVideoElement>;
  scale?: boolean;
  tooltip?: string;
  visible?: boolean;
}

const CasinoGameIcon: React.FC<Props> = ({
  icon = CasinoIcon.icnSquare,
  preload,
  game,
  race,
  scale = true,
  disabled,
  tooltip,
  ref,
  visible,
  FSBPlaceholder,
  ...rest
}) => {
  const isMobile = useSelector(select.device.isMobile);
  const isTouchSupported = useTouchSupported();
  const videoRef = React.createRef<HTMLVideoElement>();
  const { images = {}, name = '' } = game || {};
  const { liveRaceImgUrl = null, multiGameList = [] } = race || {};
  const legacyIcon = icon === CasinoIcon.icnRace;
  const iconFallback = useMemo(() => getIconFallback(icon), [icon]);
  const fallback = FSBPlaceholder ? FSBIcon : iconFallback;
  const animatedSrc = iconMeta[icon].animated && images[icon];
  const width = useMemo(() => getIconWidth(icon, isMobile), [icon, isMobile]);
  const gameIcon = useMemo(() => getRenderIcon(icon, isMobile, images), [icon, isMobile, images]);
  let src = liveRaceImgUrl || gameIcon || fallback;

  if (width && src !== fallback && scale) {
    const url = new URL(src);
    if (game) {
      url.set('query', {
        imwidth: Math.floor(width).toString(),
        id: `${game.gametype}${game.variant}`
      });
    }
    src = url.toString();
  }

  const props = { className: styles.icon, src };
  const fallbackElem = <img {...props} alt={tooltip || name} src={fallback} />;
  const containerProps: React.HTMLAttributes<HTMLDivElement> = {};
  let iconElem;

  if (!isMobile && animatedSrc && needsVideoTag(animatedSrc)) {
    iconElem = <video {...props} ref={videoRef} />;
    containerProps.onMouseEnter = () => videoRef.current?.play();
    containerProps.onMouseLeave = () => {
      videoRef.current?.pause();
      videoRef.current && (videoRef.current.currentTime = 0);
    };
  } else if (preload) {
    // suppress hydration warning due to protocol mismatch (// on server vs https:// on client)
    iconElem = <img {...props} alt={tooltip || name} suppressHydrationWarning />;
  } else if (!liveRaceImgUrl && !legacyIcon && multiGameList?.length > 1) {
    let image, imageURL;
    const imageList: string[] =
      multiGameList?.map((x) => {
        image = get(x.images, [icon], fallback);
        imageURL = new URL(image);
        imageURL.set('query', {
          imwidth: Math.floor(width).toString(),
          id: `${game?.gametype}${game?.variant}`
        });
        return imageURL.toString();
      }) || [];
    iconElem = <CasinoMultiGameImage multiGameList={imageList} alt={tooltip || name} />;
  } else {
    iconElem = (
      <LazyImage
        {...props}
        fallback={fallbackElem}
        loader={fallbackElem}
        visible={visible}
        alt={tooltip || name}
      />
    );
  }

  useEffect(() => {
    const { current: videoElement } = videoRef;
    videoElement?.setAttribute('muted', '');
  }, [videoRef]);

  return (
    <div
      onClick={rest.onClick}
      className={classNames(
        styles.container,
        (!game || disabled) && styles.disabled,
        isTouchSupported && styles.touch,
        rest.className
      )}
      {...containerProps}
    >
      {src === fallback && <div className={styles.iconLabel}>{name}</div>}
      {iconElem}
      {rest.children}
    </div>
  );
};

export default CasinoGameIcon;
